import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { z } from 'zod';
import type { assetSchema } from './schemas';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as currency
 * @param value The number to format
 * @param currency The currency code (default: USD)
 * @param locale The locale code (default: en-US)
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: number | null | undefined,
  currency = 'USD',
  locale = 'en-US',
): string => {
  // Handle null, undefined, or NaN values
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '$0.00';
  }

  // Ensure value is a number
  const numValue = typeof value === 'number' ? value : Number(value);

  // If conversion failed and resulted in NaN, return $0.00
  if (Number.isNaN(numValue)) {
    return '$0.00';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/**
 * Format a date string
 * @param dateString The date string to format
 * @param options Formatting options
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
): string => {
  if (!dateString) return 'N/A';

  try {
    let date: Date;
    // If dateString matches YYYY-MM-DD, parse as local date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateString);
    }
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format a number with commas and optional decimal places
 * @param value The number to format
 * @param decimalPlaces Number of decimal places (default: 0)
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number | null | undefined,
  decimalPlaces = 0,
): string => {
  if (value === null || value === undefined) return 'N/A';

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value);
};

/**
 * Format a number as a percentage
 * @param value The number to format (e.g., 0.25 for 25%)
 * @param decimalPlaces Number of decimal places (default: 0)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number | null | undefined,
  decimalPlaces = 0,
): string => {
  if (value === null || value === undefined) return 'N/A';

  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value / 100);
};

/**
 * Truncate a string to a specific length with an ellipsis
 */
export function truncateString(str: string, length: number): string {
  if (str.length <= length) return str;
  return `${str.slice(0, length)}...`;
}

/**
 * Calculate mortgage payment
 * @param principal Loan principal
 * @param rate Annual interest rate (as a decimal, e.g., 0.05 for 5%)
 * @param termYears Loan term in years
 * @returns Monthly payment amount
 */
export function calculateMortgagePayment(
  principal: number,
  rate: number,
  termYears: number,
): number {
  // Convert annual rate to monthly rate
  const monthlyRate = rate / 12;
  // Convert years to months
  const termMonths = termYears * 12;

  // Calculate payment using the formula:
  // M = P [ r(1+r)^n ] / [ (1+r)^n - 1 ]
  // where:
  // M = monthly payment
  // P = principal loan amount
  // r = monthly interest rate
  // n = number of months

  if (rate === 0) {
    // Handle special case of zero interest
    return principal / termMonths;
  }

  const numerator = monthlyRate * (1 + monthlyRate) ** termMonths;
  const denominator = (1 + monthlyRate) ** termMonths - 1;
  return principal * (numerator / denominator);
}

/**
 * Calculate capitalization rate (cap rate)
 * @param noi Net Operating Income (annual)
 * @param propertyValue Property value
 * @returns Cap rate as a percentage
 */
export function calculateCapRate(noi: number, propertyValue: number): number {
  if (propertyValue === 0) return 0;
  return (noi / propertyValue) * 100;
}

/**
 * Calculate cash-on-cash return
 * @param annualCashFlow Annual cash flow
 * @param totalInvestment Total cash invested
 * @returns Cash-on-cash return as a percentage
 */
export function calculateCashOnCash(
  annualCashFlow: number,
  totalInvestment: number,
): number {
  if (totalInvestment === 0) return 0;
  return (annualCashFlow / totalInvestment) * 100;
}

/**
 * Generate a random color based on a string (e.g., for avatar backgrounds)
 * @param str Input string
 * @returns Hex color code
 */
export function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

/**
 * Determine if a string has sufficient contrast against white
 * @param hexColor Hex color code
 * @returns True if the color has good contrast with white
 */
export function hasGoodContrastWithWhite(hexColor: string): boolean {
  // Convert hex to RGB
  const r = Number.parseInt(hexColor.slice(1, 3), 16);
  const g = Number.parseInt(hexColor.slice(3, 5), 16);
  const b = Number.parseInt(hexColor.slice(5, 7), 16);

  // Calculate luminance
  // Using the formula: 0.299*R + 0.587*G + 0.114*B
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return true if the luminance is below 0.5 (darker colors)
  return luminance < 0.5;
}

/**
 * Parse CSV data into an array of objects
 * @param csvData Raw CSV data as string
 * @param hasHeader Whether the CSV has a header row
 * @returns Array of objects (if hasHeader is true) or arrays
 */
export function parseCSV(
  csvData: string,
  hasHeader = true,
): Array<Record<string, string>> | Array<Array<string>> {
  const lines = csvData.trim().split('\n');

  if (lines.length === 0) {
    return [];
  }

  // Process header row
  const headers = hasHeader ? lines[0].split(',').map((h) => h.trim()) : [];

  // Process data rows
  const dataStartIndex = hasHeader ? 1 : 0;
  const results = [];

  for (let i = dataStartIndex; i < lines.length; i++) {
    const values = lines[i].split(',').map((v) => v.trim());

    if (hasHeader) {
      const obj: Record<string, string> = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] || '';
      });
      results.push(obj);
    } else {
      results.push(values);
    }
  }

  return results;
}

/**
 * Convert a value to title case
 * @param str Input string
 * @returns Title-cased string
 */
export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper function to map external asset type to our enum
export const mapAssetType = (
  externalType: string,
): z.infer<typeof assetSchema.shape.asset_type> | null => {
  const mapping: Record<
    string,
    z.infer<typeof assetSchema.shape.asset_type>
  > = {
    SingleFamily: 'rental',
    Apartment: 'rental',
    Condo: 'rental',
    Commercial: 'syndication',
    Multifamily: 'rental',
    Land: 'for_sale',
    Vacation: 'short_term_rental',
  };

  return mapping[externalType] || null;
};

export const valueFormatter = (number: number) =>
  typeof number === 'number' && !Number.isNaN(number)
    ? `$${number.toLocaleString()}`
    : '';

export const formatMonth = (monthStr: string) => {
  const date = new Date(monthStr);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export const formatYAxis = (value: number) => {
  return `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

export function formatNumberWithCommas(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('en-US').format(value);
}

export function parseNumberWithCommas(value: string): number | null {
  if (!value) return null;
  // Remove commas and convert to number
  const cleanValue = value.replace(/,/g, '');
  const parsed = Number.parseFloat(cleanValue);
  return Number.isNaN(parsed) ? null : parsed;
}
